import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@iupics-config/app.config.service';
import { catchError, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { IupicsWidget } from '@iupics-manager/models/iupics-widget';
import { ComboOption } from './widget-editor-combo.component';

/** language the names are edited in; baseLanguage false: the name saved is the translation */
export interface EditorInfo {
  language: string;
  baseLanguage: boolean;
  isAdmin: boolean;
}

/** a widget of the library (administrators) */
export interface LibraryWidget {
  id: number;
  name: string;
  widgetType: string;
  userId: number;
  userName: string | null;
  roleId: number;
  roleName: string | null;
  updated: string | null;
  dashboardItems: number;
  dashboards: number;
  sharedRoles: string[];
}

export interface EditorTab {
  id: number;
  name: string;
  tableId: number;
  level: number;
}

export interface EditorColumn {
  id: number;
  name: string;
  columnName: string;
  displayType: number;
  /** text, date, yes-no, list...: only counted (and min / max on charts) */
  textLike: boolean;
}

/** Lookups of the widget editor (ADWidgetWs /widgetEditor/*), role access applied by the server */
@Injectable({ providedIn: 'root' })
export class WidgetEditorLookupService {
  readonly #http = inject(HttpClient);
  readonly #config = inject(AppConfig);
  readonly #security = inject(SecurityManagerService);
  // tabs and columns change only with the dictionary: kept per role (access differs), errors not kept
  readonly #tabs = new Map<string, Observable<EditorTab[]>>();
  readonly #columns = new Map<string, Observable<EditorColumn[]>>();

  get #url(): string {
    return this.#config.getBackendResource('widgetEditorLookup');
  }

  windows(q: string, ids: number[] = []): Observable<ComboOption[]> {
    return this.#http
      .get<{ id: number; name: string }[]>(`${this.#url}/windows`, { params: this.#params(q, ids) })
      .pipe(map((rows) => rows.map((r) => ({ id: r.id, name: r.name }))));
  }

  tables(q: string, ids: number[] = []): Observable<ComboOption[]> {
    return this.#http
      .get<{ id: number; name: string; tableName: string }[]>(`${this.#url}/tables`, { params: this.#params(q, ids) })
      .pipe(map((rows) => rows.map((r) => ({ id: r.id, name: r.name, hint: r.tableName }))));
  }

  tabs(windowId: number): Observable<EditorTab[]> {
    if (!(windowId > 0)) return of([]);
    const key = `${this.#roleId()}:${windowId}`;
    let tabs$ = this.#tabs.get(key);
    if (!tabs$) {
      tabs$ = this.#http
        .get<EditorTab[]>(`${this.#url}/tabs`, { params: new HttpParams({ fromObject: { windowId: String(windowId) } }) })
        .pipe(this.#forgetOnError(this.#tabs, key), shareReplay({ bufferSize: 1, refCount: false }));
      this.#tabs.set(key, tabs$);
    }
    return tabs$;
  }

  columns(tabId: number | null, tableId: number | null): Observable<EditorColumn[]> {
    if (!(tabId > 0) && !(tableId > 0)) return of([]);
    const key = `${this.#roleId()}:` + (tabId > 0 ? `tab:${tabId}` : `table:${tableId}`);
    let columns$ = this.#columns.get(key);
    if (!columns$) {
      const fromObject: Record<string, string> = tabId > 0 ? { tabId: String(tabId) } : { tableId: String(tableId) };
      columns$ = this.#http
        .get<EditorColumn[]>(`${this.#url}/columns`, { params: new HttpParams({ fromObject }) })
        .pipe(this.#forgetOnError(this.#columns, key), shareReplay({ bufferSize: 1, refCount: false }));
      this.#columns.set(key, columns$);
    }
    return columns$;
  }

  info(): Observable<EditorInfo> {
    return this.#http.get<EditorInfo>(`${this.#url}/info`);
  }

  /** roles among roleIds that would see the widget empty */
  rolesWithoutAccess(widget: IupicsWidget, roleIds: number[]): Observable<number[]> {
    return this.#http.post<number[]>(`${this.#url}/roleAccess`, { widget, roleIds });
  }

  library(): Observable<LibraryWidget[]> {
    return this.#http.get<LibraryWidget[]>(`${this.#url}/library`);
  }

  /** a widget of another user (not in the own list) */
  libraryWidget(id: number): Observable<IupicsWidget> {
    return this.#http.get<IupicsWidget>(`${this.#url}/library/widget`, { params: new HttpParams({ fromObject: { id: String(id) } }) });
  }

  /** all or none */
  deleteWidgets(ids: number[]): Observable<{ deleted: number[] }> {
    return this.#http.post<{ deleted: number[] }>(`${this.#url}/library/delete`, { ids });
  }

  /** a failed call is not kept (next attempt asks again) */
  forget(): void {
    this.#tabs.clear();
    this.#columns.clear();
  }

  #roleId(): number {
    return this.#security.getIupicsUserAccount()?.current_role?.role_id ?? -1;
  }

  #forgetOnError<T>(cache: Map<string, Observable<T>>, key: string) {
    return catchError<T, Observable<T>>((err) => {
      cache.delete(key);
      return throwError(() => err);
    });
  }

  #params(q: string, ids: number[]): HttpParams {
    const fromObject: Record<string, string> = {};
    if (ids.length) fromObject['ids'] = ids.join(',');
    else if (q) fromObject['q'] = q;
    return new HttpParams({ fromObject });
  }
}
